import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Crown, User, Menu, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import PricingModal from './PricingModal';
import { billingService } from '../services/billing';
import { cn } from '@/lib/utils';
import { LogoText } from './ui/Logo';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { extractErrorMessage } from '../utils/errorUtils';
import { supabase } from '@/lib/supabase';
import { useBusinessAnalytics } from '../utils/analytics';
import DashboardSpaceSwitch from './DashboardSpaceSwitch';
import {
  SETTINGS_NAV_GROUPS,
  settingsNavSectionLabel,
  settingsPath,
} from '../settings/settingsNavConfig';

type NavUser = {
  email?: string | null;
  user_metadata?: {
    full_name?: string;
    name?: string;
    tier?: string;
  };
};

function resolveDisplayFirstName(user: NavUser | null | undefined): string {
  if (!user) return '';
  const fullName = user.user_metadata?.full_name?.trim() ?? '';
  const name = user.user_metadata?.name?.trim() ?? '';
  const fromFull = fullName ? fullName.split(/\s+/)[0] : '';
  const fromName = name ? name.split(/\s+/)[0] : '';
  const fromEmail = user.email?.split('@')[0]?.trim() ?? '';
  return (fromFull || fromName || fromEmail || '').trim();
}

function avatarInitialFromDisplayName(display: string): string {
  const t = display.trim();
  if (!t) return '';
  try {
    const seg = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    for (const { segment } of seg.segment(t)) {
      const s = segment.trim();
      if (!s) continue;
      return s.length === 1 ? s.toLocaleUpperCase() : s.slice(0, 1);
    }
  } catch {
    /* Intl.Segmenter unavailable */
  }
  return t.charAt(0).toLocaleUpperCase();
}

const NavBar: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated, logout, user } = useAuth();
  const { t } = useTranslation();
  const { t: settingsT } = useTranslation('settings');
  const { trackUpgradeClick } = useBusinessAnalytics();
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const displayFirstName = useMemo(() => resolveDisplayFirstName(user), [user]);
  const avatarInitial = useMemo(
    () => (displayFirstName ? avatarInitialFromDisplayName(displayFirstName) : ''),
    [displayFirstName]
  );

  const handleSelectPlan = async (planId: string, billingCycle: 'monthly' | 'annual' = 'monthly', couponCode?: string) => {
    try {
      if (planId === 'enterprise') {
        billingService.contactEnterpriseSales();
      } else if (planId !== 'free') {
        await billingService.upgradeTier(planId, billingCycle, couponCode);
      }
      setShowPricingModal(false);
    } catch (error) {
      console.error('Error upgrading plan:', error);
      toast.error(t('billing.checkoutErrorTitle', { ns: 'pricing' }), {
        description: extractErrorMessage(
          error,
          t('billing.checkoutErrorDescription', { ns: 'pricing' })
        ),
      });
    }
  };

  const [userTier, setUserTier] = useState(user?.user_metadata?.tier || 'free');

  useEffect(() => {
    const currentUserTier = user?.user_metadata?.tier || 'free';
    if (currentUserTier !== userTier) {
      console.log(`🔄 NavBar: User tier updated from ${userTier} to ${currentUserTier}`);
      setUserTier(currentUserTier);
    }
  }, [user, userTier]);

  useEffect(() => {
    const refreshUserData = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const hasSuccessParam = urlParams.get('success') === 'true';
        const hasSessionId = urlParams.get('session_id');

        if (hasSuccessParam || hasSessionId) {
          const { data } = await supabase.auth.refreshSession();
          if (data?.user) {
            setUserTier(data.user.user_metadata?.tier || 'free');
            console.log('🔄 User data refreshed after checkout:', data.user.user_metadata?.tier);
          }
        }
      } catch (error) {
        console.error('Failed to refresh user data:', error);
      }
    };

    refreshUserData();
  }, [location.search]);

  const openPricingFromNav = () => {
    trackUpgradeClick({
      source: 'navbar',
      current_tier: userTier,
      target_tier: 'pro',
      upgrade_context: 'general',
      element_type: 'button',
      trigger_reason: 'navbar_upgrade_click',
    });
    setShowPricingModal(true);
  };

  return (
    <>
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center gap-2">
              <Link
                to={isAuthenticated ? "/dashboard" : "/"}
                className="transition-opacity hover:opacity-80"
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <LogoText
                        variant="dark"
                        size="md"
                        iconSize={32}
                        className="text-foreground"
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t('logoTooltip', { ns: 'navigation' })}</p>
                      <Link
                        to="/blog/why-our-logo-is-clarks-nutcracker"
                        className="mt-1 block text-primary hover:underline font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t('logoWhyLink', { ns: 'navigation' })} →
                      </Link>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Link>
              <Link
                to="/blog/why-our-logo-is-clarks-nutcracker"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors hidden sm:inline"
              >
                {t('logoWhyLink', { ns: 'navigation' })}
              </Link>
              {isAuthenticated && (
                <DashboardSpaceSwitch className="flex-shrink-0 ml-2" variant="dashboard" />
              )}
            </div>

            <div className="flex items-center gap-2">
              <div className="hidden md:flex items-center space-x-4">
                {isAuthenticated ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-10 gap-2 px-2 text-foreground"
                        aria-label={t('accountMenu.open', { ns: 'navigation' })}
                      >
                        <div
                          className={cn(
                            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                            'bg-primary text-primary-foreground'
                          )}
                        >
                          {avatarInitial ? (
                            <span className="text-xs font-medium">{avatarInitial}</span>
                          ) : (
                            <User className="h-4 w-4" aria-hidden />
                          )}
                        </div>
                        <span className="max-w-[10rem] truncate text-sm font-medium">
                          {displayFirstName}
                        </span>
                        <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[14rem]">
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onSelect={() => {
                          openPricingFromNav();
                        }}
                      >
                        <Crown className="h-4 w-4" aria-hidden />
                        {userTier === 'free'
                          ? t('upgrade', { ns: 'navigation' })
                          : t('plan', { ns: 'navigation' })}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {SETTINGS_NAV_GROUPS.map((group) => (
                        <DropdownMenuGroup key={group.groupLabelKey}>
                          <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground">
                            {settingsT(group.groupLabelKey)}
                          </DropdownMenuLabel>
                          {group.items.map(({ segment, icon: Icon }) => (
                            <DropdownMenuItem key={segment} asChild>
                              <Link to={settingsPath(segment)} className="cursor-pointer">
                                <Icon className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                                {settingsNavSectionLabel(settingsT, segment)}
                              </Link>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuGroup>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onSelect={() => {
                          logout();
                        }}
                      >
                        {t('signOut', { ns: 'auth' })}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <>
                    <Button variant="ghost" size="sm" asChild className="min-h-[44px]">
                      <Link to="/docs">{t('menu.docs', { ns: 'navigation' })}</Link>
                    </Button>
                    <Button variant="ghost" size="sm" asChild className="min-h-[44px]">
                      <Link to="/pricing">{t('menu.pricing', { ns: 'navigation' })}</Link>
                    </Button>
                    <Button asChild size="sm" className="min-h-[44px]">
                      <Link to="/login">{t('signIn', { ns: 'auth' })}</Link>
                    </Button>
                  </>
                )}
              </div>

              <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="md:hidden min-h-[44px] min-w-[44px]"
                    aria-label={t('openMainMenu', { ns: 'navigation' })}
                  >
                    <Menu className="h-5 w-5" aria-hidden />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full max-w-sm">
                  <SheetHeader className="sr-only">
                    <SheetTitle>{t('mainNavAriaLabel', { ns: 'navigation' })}</SheetTitle>
                  </SheetHeader>
                  <nav
                    className="flex flex-col gap-4 pt-4"
                    aria-label={t('mainNavAriaLabel', { ns: 'navigation' })}
                  >
                    <Link
                      to={isAuthenticated ? '/dashboard' : '/'}
                      className="font-medium hover:text-primary"
                      onClick={() => setMobileNavOpen(false)}
                    >
                      <LogoText variant="dark" size="md" iconSize={32} className="inline" />
                    </Link>
                    <Link
                      to="/blog/why-our-logo-is-clarks-nutcracker"
                      className="text-sm text-muted-foreground hover:text-foreground"
                      onClick={() => setMobileNavOpen(false)}
                    >
                      {t('logoWhyLink', { ns: 'navigation' })}
                    </Link>
                    {isAuthenticated && (
                      <div className="py-2" onClick={() => setMobileNavOpen(false)}>
                        <DashboardSpaceSwitch variant="dashboard" />
                      </div>
                    )}
                    <div className="border-t pt-4 flex flex-col gap-2">
                      {isAuthenticated ? (
                        <>
                          <div className="flex items-center gap-2 py-2">
                            <div
                              className={cn(
                                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground'
                              )}
                            >
                              {avatarInitial ? (
                                <span className="text-xs font-medium">{avatarInitial}</span>
                              ) : (
                                <User className="h-4 w-4" aria-hidden />
                              )}
                            </div>
                            <span className="truncate text-sm font-medium">{displayFirstName}</span>
                          </div>
                          <Button
                            variant="ghost"
                            className="justify-start min-h-[44px]"
                            onClick={() => {
                              openPricingFromNav();
                              setMobileNavOpen(false);
                            }}
                          >
                            <Crown className="mr-2 h-4 w-4" aria-hidden />
                            {userTier === 'free'
                              ? t('upgrade', { ns: 'navigation' })
                              : t('plan', { ns: 'navigation' })}
                          </Button>
                          <div className="flex flex-col gap-4 pt-1">
                            {SETTINGS_NAV_GROUPS.map((group) => (
                              <div key={group.groupLabelKey} className="flex flex-col gap-1">
                                <p className="px-2 text-xs font-semibold text-muted-foreground">
                                  {settingsT(group.groupLabelKey)}
                                </p>
                                {group.items.map(({ segment, icon: Icon }) => (
                                  <Button
                                    key={segment}
                                    variant="ghost"
                                    className="justify-start min-h-[44px]"
                                    asChild
                                  >
                                    <Link
                                      to={settingsPath(segment)}
                                      onClick={() => setMobileNavOpen(false)}
                                    >
                                      <Icon className="mr-2 h-4 w-4 shrink-0 opacity-70" aria-hidden />
                                      {settingsNavSectionLabel(settingsT, segment)}
                                    </Link>
                                  </Button>
                                ))}
                              </div>
                            ))}
                          </div>
                          <Button
                            variant="ghost"
                            className="justify-start min-h-[44px]"
                            onClick={() => {
                              logout();
                              setMobileNavOpen(false);
                            }}
                          >
                            {t('signOut', { ns: 'auth' })}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button variant="ghost" asChild className="justify-start min-h-[44px]">
                            <Link to="/blog" onClick={() => setMobileNavOpen(false)}>
                              {t('blog', { ns: 'footer' })}
                            </Link>
                          </Button>
                          <Button variant="ghost" asChild className="justify-start min-h-[44px]">
                            <Link to="/docs" onClick={() => setMobileNavOpen(false)}>
                              {t('menu.docs', { ns: 'navigation' })}
                            </Link>
                          </Button>
                          <Button variant="ghost" asChild className="justify-start min-h-[44px]">
                            <Link to="/pricing" onClick={() => setMobileNavOpen(false)}>
                              {t('menu.pricing', { ns: 'navigation' })}
                            </Link>
                          </Button>
                          <Button asChild className="justify-start min-h-[44px]">
                            <Link to="/login" onClick={() => setMobileNavOpen(false)}>
                              {t('signIn', { ns: 'auth' })}
                            </Link>
                          </Button>
                        </>
                      )}
                    </div>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      <PricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        onSelectPlan={handleSelectPlan}
        currentTier={userTier}
      />
    </>
  );
};

export default NavBar;
